import axios, {HttpStatusCode} from "axios";
import {useRef} from "react";
import {v4 as uuidv4, v5 as uuidv5} from "uuid";
import {BaseMutationOptions, BaseUseMutationResult, useBaseMutation} from "./useBaseMutation";

export type IdempotentPaymentFn<Data, Variables> = (variables: Variables, idempotencyKey: string) => Promise<Data>;

const isOutcomeUnknown = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || status >= HttpStatusCode.InternalServerError;
};

export const useIdempotentPayment = <Data, Variables>(
  paymentFn: IdempotentPaymentFn<Data, Variables>,
  options?: BaseMutationOptions<Data, Variables>
): BaseUseMutationResult<Data, Variables> => {
  const attemptId = useRef(uuidv4());

  return useBaseMutation<Data, Variables>(async (variables: Variables) => {
    const idempotencyKey = uuidv5(JSON.stringify(variables), attemptId.current);
    try {
      const data = await paymentFn(variables, idempotencyKey);
      attemptId.current = uuidv4();
      return data;
    } catch (error) {
      if (!isOutcomeUnknown(error)) {
        attemptId.current = uuidv4();
      }
      throw error;
    }
  }, options);
};
